import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, Play, Star, Calendar, Clock, Film, Tv, Home, X, Download, ArrowLeft,
  ChevronDown, Zap, Flame, CalendarClock, Heart, Sun, Moon, Bookmark, Filter,
  ChevronRight
} from 'lucide-react';
import { ThemeProvider, useTheme, ModeProvider, useMode } from './contexts/AppContext';
import {
  API_BASE, TMDB_API_BASE, TMDB_API_KEY, JIKAN_API, ANIBHINDI_API,
  EMBEDMASTER, POVSCRAPER, EP_RANGE_SIZE,
  isUpcoming, fetchWithRetry, anilistFetch,
  getCW, saveCW, getWatchlist, isInWatchlist, toggleWatchlistItem,
  Q_TRENDING, Q_POPULAR, Q_PREVIEW, Q_SEARCH, Q_DETAILS,
  mapAnilistItem, tmdbDetailsCache, tmdbSearch, MOVIE_GENRES
} from './lib/api';

// ═══════════════════════════════════════════════════
// App Root
// ═══════════════════════════════════════════════════
export default function App() {
  const [showV2, setShowV2] = useState(() => {
    try {
      const seen = localStorage.getItem('pm_v2_popup_seen');
      if (seen === '1') return false;
      return true;
    } catch { return false; }
  });
  const closeV2 = () => {
    setShowV2(false);
    try { localStorage.setItem('pm_v2_popup_seen', '1'); } catch {}
  };

  return (
    <ThemeProvider>
      <ModeProvider>
        <div className="pm-root">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/watch/:itemId" element={<WatchPage />} />
              <Route path="/watchlist" element={<WatchlistPage />} />
              <Route path="/anime" element={<AnimeHomePage />} />
              <Route path="/anime/search" element={<AnimeSearchPage />} />
              <Route path="/anime/watch/:animeId" element={<AnimeWatchPage />} />
              <Route path="/anime/watchlist" element={<AnimeWatchlistPage />} />
            </Routes>
          </BrowserRouter>
          {showV2 && <V2Popup onClose={closeV2} />}
        </div>
      </ModeProvider>
    </ThemeProvider>
  );
}

// ═══════════════════════════════════════════════════
// Skeleton Components
// ═══════════════════════════════════════════════════
function CardSkeleton() {
  return (
    <div data-testid="card-skeleton" className="rounded-xl overflow-hidden" style={{ background: 'rgba(var(--surface2))' }}>
      <div className="skeleton-pulse" style={{ aspectRatio: '2/3', background: 'rgba(var(--border), 0.06)' }} />
      <div className="p-3 space-y-2">
        <div className="skeleton-pulse h-3 w-4/5 rounded" />
        <div className="skeleton-pulse h-2.5 w-1/2 rounded" />
      </div>
    </div>
  );
}

function GridSkeleton({ count = 12 }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
      {Array.from({ length: count }).map((_, i) => <CardSkeleton key={i} />)}
    </div>
  );
}

function EpisodeSkeleton() {
  return (
    <div className="ep-row" style={{ borderColor: 'transparent' }}>
      <div className="skeleton-pulse" style={{ width: 96, aspectRatio: '16/9' }} />
      <div className="p-3 space-y-2 flex flex-col justify-center">
        <div className="skeleton-pulse h-2 w-1/4 rounded" />
        <div className="skeleton-pulse h-3 w-3/4 rounded" />
        <div className="skeleton-pulse h-2 w-1/2 rounded" />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Header
// ═══════════════════════════════════════════════════
function Header({ sq, setSq, onSearch, mobileOpen, setMobileOpen, showDrop, setShowDrop, mobileRef, previewResults, onItemClick, activeTab, isAnime }) {
  const navigate = useNavigate();
  const { setAnimeMode } = useMode();
  const { isDark, toggleTheme } = useTheme();

  const switchMode = (toAnime) => {
    setAnimeMode(toAnime);
    navigate(toAnime ? '/anime' : '/');
  };

  const Dropdown = () => (
    <div
      data-testid="search-dropdown"
      className="absolute top-full mt-2 left-0 right-0 rounded-xl overflow-hidden shadow-2xl z-[400]"
      style={{
        background: 'rgb(var(--surface))',
        border: '1px solid rgba(var(--border), var(--border-a))',
      }}
    >
      {previewResults.slice(0, 5).map(item => (
        <div
          key={item.id}
          data-testid="search-result-item"
          className="flex items-center gap-3 px-3.5 py-2.5 cursor-pointer transition-colors duration-150 hover:bg-[rgba(var(--border),0.05)]"
          style={{ borderBottom: '1px solid rgba(var(--border), 0.04)' }}
          onClick={() => { onItemClick(item); setShowDrop(false); setMobileOpen(false); }}
        >
          <img
            src={item.image || '/tlogo.png'}
            alt={item.title}
            className="w-9 h-[52px] object-cover rounded flex-shrink-0"
            style={{ background: 'rgba(var(--border), 0.06)' }}
            loading="lazy"
            onError={e => { e.target.src = '/tlogo.png'; }}
          />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[13px] leading-tight mb-1 line-clamp-2" style={{ color: 'rgb(var(--fg))' }}>
              {item.title}
            </div>
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider uppercase font-mono"
                style={{
                  background: isAnime ? 'rgba(var(--anime), 0.12)' : item.type === 'Movie' ? 'rgba(var(--crimson), 0.1)' : 'rgba(var(--blue), 0.1)',
                  color: isAnime ? 'rgb(var(--anime))' : item.type === 'Movie' ? 'rgb(var(--crimson))' : 'rgb(var(--blue))',
                }}
              >
                {isAnime ? <Zap size={8} /> : item.type === 'Movie' ? <Film size={8} /> : <Tv size={8} />}
                {isAnime ? (item.format || 'Anime') : item.type}
              </span>
              {item.releaseDate && (
                <span className="text-[11px] font-mono" style={{ color: 'rgb(var(--muted))' }}>
                  {item.releaseDate}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <header
      data-testid="app-header"
      className="glass-header sticky top-0 z-[200] transition-all duration-300"
      style={{ borderBottom: `1px solid ${isAnime ? 'rgba(var(--anime), 0.15)' : 'rgba(var(--border), var(--border-a))'}` }}
    >
      <div className="max-w-[1360px] mx-auto px-3 md:px-6 flex items-center gap-2 md:gap-3" style={{ height: 'var(--hdr)' }}>
        {/* Logo */}
        <div
          data-testid="logo"
          className="flex items-center gap-1.5 md:gap-2 flex-shrink-0 cursor-pointer min-w-0"
          onClick={() => navigate(isAnime ? '/anime' : '/')}
        >
          <img src="/tlogo.png" alt="" className="w-6 h-6 md:w-7 md:h-7 object-contain rounded-md flex-shrink-0" loading="lazy" />
          <span className={`hidden sm:inline font-heading font-black italic text-xl tracking-wide uppercase leading-none ${isAnime ? 'text-gradient-anime' : 'text-gradient-crimson'}`}>
            povertymovie
          </span>
        </div>

        {/* Mode Toggle */}
        <div
          className="flex items-center flex-shrink-0 rounded-lg p-0.5 gap-0.5"
          style={{ background: 'rgba(var(--border), 0.06)', border: '1px solid rgba(var(--border), var(--border-a))' }}
        >
          <button
            data-testid="mode-movies-btn"
            className="flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1.5 rounded-md font-heading text-xs font-semibold transition-all duration-200 whitespace-nowrap"
            style={{
              background: !isAnime ? 'rgba(var(--crimson), 0.15)' : 'transparent',
              color: !isAnime ? 'rgb(var(--fg))' : 'rgb(var(--muted))',
            }}
            onClick={() => switchMode(false)}
          >
            <Film size={12} />
            <span className="hidden sm:inline">Movies & TV</span>
          </button>
          <button
            data-testid="mode-anime-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md font-heading text-xs font-semibold transition-all duration-200 whitespace-nowrap"
            style={{
              background: isAnime ? 'rgba(var(--anime), 0.18)' : 'transparent',
              color: isAnime ? 'rgb(var(--fg))' : 'rgb(var(--muted))',
            }}
            onClick={() => switchMode(true)}
          >
            <Zap size={12} />
            <span className="hidden sm:inline">Anime</span>
          </button>
        </div>

        {/* Desktop Search */}
        <div className="hidden md:block flex-1 max-w-[420px] relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgb(var(--muted))' }} />
          <input
            data-testid="search-input"
            type="text"
            value={sq}
            onChange={e => setSq(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onSearch(e)}
            placeholder={isAnime ? 'Search anime...' : 'Search movies & TV...'}
            className="w-full h-9 pl-9 pr-3 rounded-lg font-body text-[13px] transition-all duration-200 outline-none"
            style={{
              background: 'rgba(var(--border), 0.05)',
              border: '1px solid rgba(var(--border), var(--border-a))',
              color: 'rgb(var(--fg))',
            }}
            onFocus={() => setShowDrop(true)}
            onBlur={() => setTimeout(() => setShowDrop(false), 200)}
          />
          {showDrop && previewResults.length > 0 && <Dropdown />}
        </div>

        {/* Nav */}
        <nav className="flex items-center gap-1 md:gap-1.5 ml-auto flex-shrink-0">
          <button
            data-testid="nav-home-btn"
            onClick={() => navigate(isAnime ? '/anime' : '/')}
            className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-200"
            style={{
              color: activeTab === 'home' ? 'rgb(var(--fg))' : 'rgb(var(--muted))',
              background: activeTab === 'home' ? 'rgba(var(--border), 0.06)' : 'transparent',
            }}
          >
            <Home size={15} />
            <span className="hidden sm:inline">Home</span>
          </button>

          <button
            data-testid="nav-watchlist-btn"
            onClick={() => navigate(isAnime ? '/anime/watchlist' : '/watchlist')}
            className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-200"
            style={{
              color: activeTab === 'watchlist' ? 'rgb(var(--fg))' : 'rgb(var(--muted))',
              background: activeTab === 'watchlist' ? 'rgba(var(--border), 0.06)' : 'transparent',
            }}
          >
            <Bookmark size={15} />
            <span className="hidden sm:inline">Watchlist</span>
          </button>

          {!isAnime && (
            <a
              href="https://v2.povertymovie.fun"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="v2-link"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-heading font-bold italic text-[13px] tracking-wide uppercase text-white"
              style={{
                background: 'linear-gradient(118deg, #e03010, #cc44ff)',
                boxShadow: '0 0 12px rgba(224,48,16,0.25)',
              }}
            >
              <span className="text-[8px] font-mono not-italic font-bold tracking-wider px-1 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.2)' }}>NEW</span>
              V2
            </a>
          )}

          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-lg transition-all duration-200"
            style={{
              background: 'rgba(var(--border), 0.06)',
              border: '1px solid rgba(var(--border), var(--border-a))',
              color: 'rgb(var(--muted))',
            }}
            aria-label="Toggle theme"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {/* Mobile search toggle */}
          <button
            data-testid="mobile-search-toggle"
            className="flex md:hidden items-center justify-center w-8 h-8 rounded-lg transition-all duration-200"
            style={{
              background: 'rgba(var(--border), 0.06)',
              border: '1px solid rgba(var(--border), var(--border-a))',
              color: 'rgb(var(--muted))',
            }}
            onClick={() => setMobileOpen(v => !v)}
            aria-label="Search"
          >
            <Search size={16} />
          </button>
        </nav>
      </div>

      {/* Mobile search bar */}
      {mobileOpen && (
        <div className="slide-down px-4 pb-3" style={{ borderTop: '1px solid rgba(var(--border), 0.04)' }}>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgb(var(--muted))' }} />
            <input
              ref={mobileRef}
              data-testid="mobile-search-input"
              type="text"
              value={sq}
              onChange={e => setSq(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && onSearch(e)}
              placeholder={isAnime ? 'Search anime...' : 'Search movies & TV...'}
              className="w-full h-9 pl-9 pr-3 rounded-lg font-body text-[13px] transition-all duration-200 outline-none"
              style={{
                background: 'rgba(var(--border), 0.05)',
                border: '1px solid rgba(var(--border), var(--border-a))',
                color: 'rgb(var(--fg))',
              }}
              onFocus={() => setShowDrop(true)}
              onBlur={() => setTimeout(() => setShowDrop(false), 200)}
              autoFocus
            />
            {showDrop && previewResults.length > 0 && <Dropdown />}
          </div>
        </div>
      )}
    </header>
  );
}

// ═══════════════════════════════════════════════════
// Section Header
// ═══════════════════════════════════════════════════
function SectionHead({ title, count, barColor, isAnime }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div
        className="w-1 h-6 rounded-sm flex-shrink-0"
        style={{ background: barColor || (isAnime ? 'linear-gradient(180deg, rgb(var(--anime)), rgb(var(--anime-pink)))' : 'rgb(var(--crimson))') }}
      />
      <h2 className="font-heading font-black italic text-2xl md:text-[1.65rem] tracking-tight uppercase leading-none" style={{ color: 'rgb(var(--fg))' }}>
        {title}
      </h2>
      {count !== undefined && (
        <span className="font-mono text-[10px] tracking-wider px-2 py-0.5 rounded"
          style={{ color: 'rgb(var(--muted))', background: 'rgba(var(--border), 0.06)', border: '1px solid rgba(var(--border), var(--border-a))' }}>
          {count}
        </span>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Watchlist Button (for cards)
// ═══════════════════════════════════════════════════
function WatchlistBtn({ item, isAnime, onToggle }) {
  const [inList, setInList] = useState(() => isInWatchlist(item.id, isAnime));

  const handleClick = (e) => {
    e.stopPropagation();
    toggleWatchlistItem(item, isAnime);
    setInList(!inList);
    if (onToggle) onToggle();
  };

  return (
    <button
      data-testid="watchlist-toggle"
      onClick={handleClick}
      className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 z-10"
      style={{
        background: inList ? 'rgb(var(--crimson))' : 'rgba(0,0,0,0.7)',
        border: `1px solid ${inList ? 'rgb(var(--crimson))' : 'rgba(255,255,255,0.2)'}`,
      }}
      title={inList ? 'Remove from watchlist' : 'Add to watchlist'}
    >
      <Heart size={12} fill={inList ? 'white' : 'none'} color="white" />
    </button>
  );
}

// ═══════════════════════════════════════════════════
// Movie Card
// ═══════════════════════════════════════════════════
function MovieCard({ item, onClick, showSoonBadge }) {
  const isTV = item.type === 'TV Series' || item.type === 'TV';
  const isSoon = showSoonBadge || isUpcoming(item._rawDate);

  return (
    <div className="group relative" data-testid="movie-card">
      <div
        className="card-hover relative rounded-xl overflow-hidden cursor-pointer"
        style={{
          background: 'rgb(var(--surface))',
          border: `1px solid ${isSoon ? 'rgba(var(--gold), 0.12)' : 'rgba(var(--border), var(--border-a))'}`,
        }}
        onClick={() => onClick(item)}
      >
        <div className="relative" style={{ aspectRatio: '2/3' }}>
          {isSoon && (
            <div
              className="absolute top-2 left-2 z-10 flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[8px] font-bold tracking-widest"
              style={{ background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(var(--gold), 0.4)', color: 'rgb(var(--gold))' }}
            >
              <CalendarClock size={8} /> UPCOMING
            </div>
          )}
          <WatchlistBtn item={item} isAnime={false} />
          <img
            src={item.image || '/tlogo.png'}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={e => { e.target.src = '/tlogo.png'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
            <div className="flex items-center gap-1 font-mono text-[9px] tracking-wider uppercase mb-1"
              style={{ color: isTV ? 'rgb(var(--blue))' : 'rgb(var(--crimson))' }}>
              {isTV ? <Tv size={10} /> : <Film size={10} />}{item.type}
            </div>
            {item.releaseDate && <div className="font-mono text-[9px]" style={{ color: 'rgb(var(--muted))' }}>{item.releaseDate}</div>}
          </div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 scale-75 group-hover:scale-100"
            style={{ background: 'rgba(var(--crimson), 0.9)' }}>
            <Play size={16} fill="white" color="white" style={{ marginLeft: 2 }} />
          </div>
        </div>
        <div className="p-2.5 md:p-3">
          <div className="font-semibold text-[12.5px] leading-tight mb-1.5 line-clamp-2" style={{ color: 'rgb(var(--fg))' }}>
            {isTV && item.season && item.episodeNumber ? `${item.title} - S${item.season} E${item.episodeNumber}` : item.title}
          </div>
          <div className="flex items-center justify-between">
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[9px] font-bold tracking-wider uppercase"
              style={{
                background: isSoon ? 'rgba(var(--gold), 0.1)' : isTV ? 'rgba(var(--blue), 0.1)' : 'rgba(var(--crimson), 0.1)',
                color: isSoon ? 'rgb(var(--gold))' : isTV ? 'rgb(var(--blue))' : 'rgb(var(--crimson))',
              }}
            >
              {isSoon ? <><CalendarClock size={8} /> Soon</> : isTV ? <><Tv size={8} /> Series</> : <><Film size={8} /> Movie</>}
            </span>
            {item.rating && !isSoon && (
              <span className="flex items-center gap-1 font-mono text-[10px]" style={{ color: 'rgb(var(--gold))' }}>
                <Star size={10} fill="currentColor" />
                {typeof item.rating === 'number' ? item.rating.toFixed(1) : item.rating}
              </span>
            )}
            {isSoon && item._rawDate && (
              <span className="font-mono text-[9px]" style={{ color: 'rgb(var(--gold))' }}>
                {new Date(item._rawDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Anime Card
// ═══════════════════════════════════════════════════
function AnimeCard({ item, onClick }) {
  const label = !item.format ? 'Anime' : item.format === 'TV' ? 'Series' : item.format === 'MOVIE' ? 'Movie' : item.format.replace(/_/g, ' ');
  const isMovie = item.format === 'MOVIE';

  return (
    <div className="group relative" data-testid="anime-card">
      <div
        className="card-hover relative rounded-xl overflow-hidden cursor-pointer"
        style={{ background: 'rgb(var(--surface))', border: '1px solid rgba(var(--border), var(--border-a))' }}
        onClick={() => onClick(item)}
      >
        <div className="relative" style={{ aspectRatio: '2/3' }}>
          <WatchlistBtn item={item} isAnime={true} />
          <img
            src={item.image || '/tlogo.png'}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={e => { e.target.src = '/tlogo.png'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
            <div className="flex items-center gap-1 font-mono text-[9px] tracking-wider uppercase mb-1" style={{ color: 'rgb(var(--anime))' }}>
              <Zap size={10} />{label}
            </div>
            {item.releaseDate && <div className="font-mono text-[9px]" style={{ color: 'rgb(var(--muted))' }}>{item.releaseDate}</div>}
          </div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 scale-75 group-hover:scale-100"
            style={{ background: 'rgba(var(--anime), 0.9)' }}>
            <Play size={16} fill="white" color="white" style={{ marginLeft: 2 }} />
          </div>
        </div>
        <div className="p-2.5 md:p-3">
          <div className="font-semibold text-[12.5px] leading-tight mb-1.5 line-clamp-2" style={{ color: 'rgb(var(--fg))' }}>{item.title}</div>
          <div className="flex items-center justify-between">
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[9px] font-bold tracking-wider uppercase"
              style={{
                background: isMovie ? 'rgba(var(--anime-pink), 0.1)' : 'rgba(var(--anime), 0.12)',
                color: isMovie ? 'rgb(var(--anime-pink))' : 'rgb(var(--anime))',
              }}
            >
              <Zap size={8} />{label}
            </span>
            {item.rating && (
              <span className="flex items-center gap-1 font-mono text-[10px]" style={{ color: '#fbbf24' }}>
                <Star size={10} fill="currentColor" />{(item.rating / 10).toFixed(1)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Search Preview Hook
// ═══════════════════════════════════════════════════
function useSearchPreview(sq, isAnime) {
  const [results, setResults] = useState([]);
  const timeout = useRef(null);

  useEffect(() => {
    if (!sq.trim()) { setResults([]); return; }
    clearTimeout(timeout.current);
    timeout.current = setTimeout(async () => {
      if (isAnime) {
        try {
          const r = await anilistFetch(Q_PREVIEW, { search: sq });
          setResults((r.data?.Page?.media || []).map(m => ({
            id: m.id,
            title: m.title.english || m.title.romaji,
            image: m.coverImage?.large,
            format: m.format,
            releaseDate: m.seasonYear,
          })));
        } catch { setResults([]); }
      } else {
        try { const r = await tmdbSearch(sq, 5); setResults(r); } catch { setResults([]); }
      }
    }, 400);
    return () => clearTimeout(timeout.current);
  }, [sq, isAnime]);

  return results;
}

// ═══════════════════════════════════════════════════
// Genre Filter Bar
// ═══════════════════════════════════════════════════
function GenreFilterBar({ selectedGenre, onSelect }) {
  return (
    <div data-testid="genre-filter" className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
      <Filter size={14} style={{ color: 'rgb(var(--muted))', flexShrink: 0 }} />
      <button
        data-testid="genre-all"
        onClick={() => onSelect(null)}
        className="flex-shrink-0 px-3 py-1.5 rounded-full font-heading text-[11px] font-semibold tracking-wide transition-all duration-200"
        style={{
          background: !selectedGenre ? 'rgb(var(--crimson))' : 'rgba(var(--border), 0.06)',
          color: !selectedGenre ? 'white' : 'rgb(var(--muted))',
          border: `1px solid ${!selectedGenre ? 'rgb(var(--crimson))' : 'rgba(var(--border), var(--border-a))'}`,
        }}
      >
        All
      </button>
      {MOVIE_GENRES.map(g => (
        <button
          key={g.id}
          data-testid={`genre-${g.name.toLowerCase()}`}
          onClick={() => onSelect(selectedGenre === g.id ? null : g.id)}
          className="flex-shrink-0 px-3 py-1.5 rounded-full font-heading text-[11px] font-semibold tracking-wide transition-all duration-200"
          style={{
            background: selectedGenre === g.id ? 'rgb(var(--crimson))' : 'rgba(var(--border), 0.06)',
            color: selectedGenre === g.id ? 'white' : 'rgb(var(--muted))',
            border: `1px solid ${selectedGenre === g.id ? 'rgb(var(--crimson))' : 'rgba(var(--border), var(--border-a))'}`,
          }}
        >
          {g.name}
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Empty State
// ═══════════════════════════════════════════════════
function EmptyState({ icon: Icon, title, subtitle, isAnime }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-state">
      <Icon size={52} style={{ color: 'rgb(var(--muted))', marginBottom: 14, opacity: 0.5 }} />
      <h3 className="font-heading font-bold italic text-xl tracking-wide uppercase mb-1.5" style={{ color: 'rgb(var(--muted))' }}>{title}</h3>
      <p className="text-[13px]" style={{ color: 'rgb(var(--muted))', opacity: 0.6 }}>{subtitle}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Hero Banner - Trending This Week
// ═══════════════════════════════════════════════════
function HeroBanner({ item, onWatch, onAddWatchlist }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const isTV = item.type === 'TV Series' || item.type === 'TV';
  const [inWl, setInWl] = useState(() => isInWatchlist(item.id, false));
  const genreNames = (item.genreIds || []).map(gid => MOVIE_GENRES.find(g => g.id === gid)?.name).filter(Boolean).slice(0, 3);

  return (
    <div
      data-testid="hero-banner"
      className="relative rounded-2xl overflow-hidden mb-10 fade-in group"
      style={{
        minHeight: 'clamp(260px, 42vw, 420px)',
        background: 'rgb(var(--surface))',
        border: '1px solid rgba(var(--border), var(--border-a))',
      }}
    >
      {/* Backdrop image */}
      {item.backdrop && (
        <img
          src={item.backdrop}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
          style={{ opacity: imgLoaded ? 1 : 0 }}
          onLoad={() => setImgLoaded(true)}
          onError={e => { e.target.style.display = 'none'; }}
          loading="eager"
        />
      )}

      {/* Gradient overlays */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 40%, rgba(0,0,0,0.25) 75%, rgba(0,0,0,0.1) 100%)',
      }} />
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.7) 100%)',
      }} />

      {/* Accent glow */}
      <div className="absolute bottom-0 left-0 w-64 h-64 pointer-events-none" style={{
        background: `radial-gradient(circle at 0% 100%, rgba(var(--crimson), 0.15), transparent 60%)`,
      }} />

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-end h-full p-5 md:p-8 lg:p-10" style={{ minHeight: 'clamp(260px, 42vw, 420px)' }}>
        {/* Badge */}
        <div className="flex items-center gap-2 mb-3">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md font-mono text-[9px] font-bold tracking-[0.14em] uppercase"
            style={{
              background: 'rgba(var(--crimson), 0.18)',
              border: '1px solid rgba(var(--crimson), 0.3)',
              color: 'rgb(var(--crimson))',
            }}
          >
            <Flame size={10} /> Trending This Week
          </span>
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-mono text-[9px] font-bold tracking-wider uppercase"
            style={{
              background: isTV ? 'rgba(91,156,246,0.15)' : 'rgba(var(--crimson), 0.12)',
              color: isTV ? 'rgb(var(--blue))' : 'rgb(var(--crimson))',
              border: `1px solid ${isTV ? 'rgba(91,156,246,0.25)' : 'rgba(var(--crimson), 0.25)'}`,
            }}
          >
            {isTV ? <Tv size={9} /> : <Film size={9} />}
            {item.type}
          </span>
        </div>

        {/* Title */}
        <h1
          className="font-heading font-black italic uppercase leading-[0.95] tracking-tight mb-3 text-white max-w-2xl"
          style={{ fontSize: 'clamp(30px, 5.5vw, 60px)', textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}
          data-testid="hero-title"
        >
          {item.title}
        </h1>

        {/* Meta row */}
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          {item.rating && (
            <span className="flex items-center gap-1 font-mono text-[13px] font-semibold" style={{ color: '#fbbf24' }}>
              <Star size={14} fill="currentColor" />{item.rating.toFixed(1)}
            </span>
          )}
          {item.releaseDate && (
            <span className="flex items-center gap-1 font-mono text-[13px]" style={{ color: 'rgba(255,255,255,0.6)' }}>
              <Calendar size={13} />{item.releaseDate}
            </span>
          )}
          {genreNames.length > 0 && genreNames.map((g, i) => (
            <span key={i} className="font-mono text-[11px] px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}>
              {g}
            </span>
          ))}
        </div>

        {/* Overview */}
        {item.overview && (
          <p className="text-[14px] md:text-[15px] leading-relaxed max-w-lg mb-5 line-clamp-2 md:line-clamp-3"
            style={{ color: 'rgba(255,255,255,0.65)' }}>
            {item.overview}
          </p>
        )}

        {/* CTA buttons */}
        <div className="flex items-center gap-3">
          <button
            data-testid="hero-watch-btn"
            onClick={() => onWatch(item)}
            className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl font-heading font-bold italic text-[15px] tracking-wide uppercase text-white transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
            style={{
              background: 'linear-gradient(135deg, rgb(var(--crimson)), #cc2244)',
              boxShadow: '0 4px 24px rgba(var(--crimson), 0.35), inset 0 1px 0 rgba(255,255,255,0.1)',
            }}
          >
            <Play size={18} fill="white" style={{ marginLeft: 1 }} />
            Watch Now
          </button>
          <button
            data-testid="hero-watchlist-btn"
            onClick={() => {
              toggleWatchlistItem(item, false);
              setInWl(prev => !prev);
            }}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-heading font-semibold text-[14px] tracking-wide transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
            style={{
              background: inWl ? 'rgba(var(--crimson), 0.2)' : 'rgba(255,255,255,0.08)',
              border: `1px solid ${inWl ? 'rgba(var(--crimson), 0.4)' : 'rgba(255,255,255,0.12)'}`,
              color: inWl ? 'rgb(var(--crimson))' : 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <Heart size={16} fill={inWl ? 'currentColor' : 'none'} />
            {inWl ? 'In Watchlist' : 'Watchlist'}
          </button>
        </div>
      </div>

      {/* Poster thumbnail (desktop only) */}
      {item.image && (
        <div className="absolute right-6 lg:right-10 bottom-6 lg:bottom-8 hidden md:block z-10 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
          <img
            src={item.image}
            alt={item.title}
            className="w-[120px] lg:w-[140px] rounded-xl shadow-2xl"
            style={{ border: '2px solid rgba(255,255,255,0.08)' }}
            loading="lazy"
          />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Home Page
// ═══════════════════════════════════════════════════
function HomePage() {
  const navigate = useNavigate();
  const [trending, setTrending] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);
  const [heroItem, setHeroItem] = useState(null);
  const [sq, setSq] = useState('');
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const mobileRef = useRef(null);
  const [cw, setCw] = useState(() => getCW(false));
  const [selectedGenre, setSelectedGenre] = useState(null);
  const preview = useSearchPreview(sq, false);

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const [mRes, tRes] = await Promise.all([
          fetchWithRetry(`${TMDB_API_BASE}/trending/movie/week?api_key=${TMDB_API_KEY}`),
          fetchWithRetry(`${TMDB_API_BASE}/trending/tv/week?api_key=${TMDB_API_KEY}`),
        ]);
        const [mData, tData] = await Promise.all([mRes.json(), tRes.json()]);

        // Pick hero from top trending movie or TV (whichever has backdrop)
        const rawAll = [...(mData.results || []).map(r => ({ ...r, _type: 'movie' })), ...(tData.results || []).map(r => ({ ...r, _type: 'tv' }))];
        const heroRaw = rawAll.find(r => r.backdrop_path && r.overview && !isUpcoming(r.release_date || r.first_air_date));

        const addImdb = async (item, type) => {
          const path = type === 'movie' ? 'movie' : 'tv';
          const rawDate = item.release_date || item.first_air_date;
          try {
            const e = await fetchWithRetry(`${TMDB_API_BASE}/${path}/${item.id}/external_ids?api_key=${TMDB_API_KEY}`).then(r => r.json());
            return {
              id: e.imdb_id || `tmdb-${type}-${item.id}`,
              title: item.title || item.name,
              image: item.poster_path ? `https://image.tmdb.org/t/p/w342${item.poster_path}` : null,
              backdrop: item.backdrop_path ? `https://image.tmdb.org/t/p/w1280${item.backdrop_path}` : null,
              overview: item.overview || '',
              type: type === 'movie' ? 'Movie' : 'TV Series',
              releaseDate: rawDate?.split('-')[0],
              _rawDate: rawDate,
              rating: item.vote_average,
              isImdbId: !!e.imdb_id,
              tmdbId: item.id,
              genreIds: item.genre_ids || [],
            };
          } catch {
            return {
              id: `tmdb-${type}-${item.id}`,
              title: item.title || item.name,
              image: item.poster_path ? `https://image.tmdb.org/t/p/w342${item.poster_path}` : null,
              backdrop: item.backdrop_path ? `https://image.tmdb.org/t/p/w1280${item.backdrop_path}` : null,
              overview: item.overview || '',
              type: type === 'movie' ? 'Movie' : 'TV Series',
              releaseDate: rawDate?.split('-')[0],
              _rawDate: rawDate,
              rating: item.vote_average,
              isImdbId: false,
              genreIds: item.genre_ids || [],
            };
          }
        };

        const [movies, tvs] = await Promise.all([
          Promise.all((mData.results || []).slice(0, 10).map(m => addImdb(m, 'movie'))),
          Promise.all((tData.results || []).slice(0, 10).map(t => addImdb(t, 'tv'))),
        ]);

        // Build hero from enriched data
        if (heroRaw) {
          const enriched = [...movies, ...tvs].find(i => i.tmdbId === heroRaw.id);
          if (enriched) setHeroItem(enriched);
        }

        const all = [...movies, ...tvs].sort(() => Math.random() - 0.5);
        setTrending(all.filter(item => !isUpcoming(item._rawDate)));
        setComingSoon(all.filter(item => isUpcoming(item._rawDate)));
      } catch { setTrending([]); }
      finally { setLoading(false); }
    };
    fetchTrending();
  }, []);

  const onSearch = (e) => { if (e?.preventDefault) e.preventDefault(); if (sq.trim()) navigate(`/search?q=${encodeURIComponent(sq)}`); };
  const onItemClick = (item) => navigate(item.isImdbId ? `/watch/${item.id}?title=${encodeURIComponent(item.title)}` : `/watch/${item.id}`);
  const removeCw = (id) => { const next = getCW(false).filter(i => i.id !== id); saveCW(next, false); setCw(next); };

  const filteredTrending = selectedGenre
    ? trending.filter(item => item.genreIds?.includes(selectedGenre))
    : trending;

  return (
    <div style={{ minHeight: '100vh', background: 'rgb(var(--bg))' }} data-testid="home-page">
      <Header sq={sq} setSq={setSq} onSearch={onSearch} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}
        showDrop={showDrop} setShowDrop={setShowDrop} mobileRef={mobileRef} previewResults={preview}
        onItemClick={onItemClick} activeTab="home" isAnime={false} />

      <main className="max-w-[1360px] mx-auto px-4 md:px-6 py-8 md:py-10 pb-20">
        {/* Hero Banner */}
        {!loading && heroItem && (
          <HeroBanner item={heroItem} onWatch={onItemClick} />
        )}
        {loading && (
          <div className="relative rounded-2xl overflow-hidden mb-10 skeleton-pulse" style={{ minHeight: 'clamp(260px, 42vw, 420px)' }} />
        )}

        {/* Continue Watching */}
        {cw.length > 0 && (
          <section className="mb-10 fade-in" data-testid="continue-watching-section">
            <SectionHead title="Continue Watching" count={cw.length} />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {cw.map(item => (
                <div key={item.id + (item.episodeId || '')} className="relative group">
                  <MovieCard
                    item={item}
                    onClick={() => {
                      const p = new URLSearchParams();
                      if ((item.type === 'TV Series' || item.type === 'TV') && item.season) p.set('season', item.season);
                      if ((item.type === 'TV Series' || item.type === 'TV') && item.episodeId) p.set('episodeId', item.episodeId);
                      if (item.isImdbId) p.set('title', item.title);
                      navigate(p.toString() ? `/watch/${item.id}?${p}` : `/watch/${item.id}`);
                    }}
                  />
                  <button
                    data-testid="remove-cw-btn"
                    className="absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20"
                    style={{ background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,255,255,0.15)' }}
                    onClick={e => { e.stopPropagation(); removeCw(item.id); }}
                    aria-label="Remove"
                  >
                    <X size={10} color="white" />
                  </button>
                </div>
              ))}
            </div>
            <div className="my-8" style={{ height: 1, background: 'rgba(var(--border), var(--border-a))' }} />
          </section>
        )}

        {/* Genre Filter */}
        <GenreFilterBar selectedGenre={selectedGenre} onSelect={setSelectedGenre} />

        {/* Trending Now */}
        <section className="mb-12" data-testid="trending-section">
          <SectionHead title="Trending Now" count={!loading ? filteredTrending.length : undefined} barColor="rgb(var(--blue))" />
          {loading ? (
            <GridSkeleton count={12} />
          ) : filteredTrending.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {filteredTrending.map((item, i) => <MovieCard key={`${item.id}-${i}`} item={item} onClick={onItemClick} />)}
            </div>
          ) : (
            <EmptyState icon={Film} title="No results" subtitle="Try a different genre filter" />
          )}
        </section>

        {/* Coming Soon */}
        {!loading && comingSoon.length > 0 && (
          <section data-testid="coming-soon-section">
            <div className="mb-8" style={{ height: 1, background: 'rgba(var(--border), var(--border-a))' }} />
            <SectionHead title="Coming Soon" count={comingSoon.length} barColor="rgb(var(--gold))" />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {comingSoon.map((item, i) => <MovieCard key={`soon-${item.id}-${i}`} item={item} onClick={onItemClick} showSoonBadge />)}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Search Page
// ═══════════════════════════════════════════════════
function SearchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [sq, setSq] = useState(query);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const mobileRef = useRef(null);
  const preview = useSearchPreview(sq, false);

  useEffect(() => {
    if (!query) return;
    setLoading(true);
    tmdbSearch(query, 50).then(setResults).catch(() => setResults([])).finally(() => setLoading(false));
  }, [query]);

  const onSearch = (e) => { if (e?.preventDefault) e.preventDefault(); if (sq.trim()) navigate(`/search?q=${encodeURIComponent(sq)}`); };
  const onItemClick = (item) => navigate(item.isImdbId ? `/watch/${item.id}?title=${encodeURIComponent(item.title)}` : `/watch/${item.id}`);

  return (
    <div style={{ minHeight: '100vh', background: 'rgb(var(--bg))' }} data-testid="search-page">
      <Header sq={sq} setSq={setSq} onSearch={onSearch} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}
        showDrop={showDrop} setShowDrop={setShowDrop} mobileRef={mobileRef} previewResults={preview}
        onItemClick={onItemClick} activeTab="search" isAnime={false} />
      <main className="max-w-[1360px] mx-auto px-4 md:px-6 py-8 md:py-10 pb-20">
        <div className="mb-6">
          <h1 className="font-heading font-black italic text-2xl md:text-3xl tracking-tight uppercase leading-none mb-2" style={{ color: 'rgb(var(--fg))' }}>
            Results for <span style={{ color: 'rgb(var(--crimson))' }}>"{query}"</span>
          </h1>
          {!loading && results.length > 0 && <span className="font-mono text-[10px] tracking-wider" style={{ color: 'rgb(var(--muted))' }}>{results.length} titles found</span>}
        </div>
        {loading ? <GridSkeleton /> : results.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {results.map((item, i) => <MovieCard key={`${item.id}-${i}`} item={item} onClick={onItemClick} />)}
          </div>
        ) : <EmptyState icon={Search} title="No results found" subtitle="Try different keywords" />}
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Watchlist Page
// ═══════════════════════════════════════════════════
function WatchlistPage() {
  const navigate = useNavigate();
  const [sq, setSq] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const mobileRef = useRef(null);
  const [wl, setWl] = useState(() => getWatchlist(false));
  const preview = useSearchPreview(sq, false);

  const onSearch = (e) => { if (e?.preventDefault) e.preventDefault(); if (sq.trim()) navigate(`/search?q=${encodeURIComponent(sq)}`); };
  const onItemClick = (item) => navigate(item.isImdbId ? `/watch/${item.id}?title=${encodeURIComponent(item.title)}` : `/watch/${item.id}`);
  const refresh = () => setWl(getWatchlist(false));

  return (
    <div style={{ minHeight: '100vh', background: 'rgb(var(--bg))' }} data-testid="watchlist-page">
      <Header sq={sq} setSq={setSq} onSearch={onSearch} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}
        showDrop={showDrop} setShowDrop={setShowDrop} mobileRef={mobileRef} previewResults={preview}
        onItemClick={onItemClick} activeTab="watchlist" isAnime={false} />
      <main className="max-w-[1360px] mx-auto px-4 md:px-6 py-8 md:py-10 pb-20">
        <SectionHead title="Your Watchlist" count={wl.length} />
        {wl.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {wl.map(item => (
              <div key={item.id} className="relative group">
                <MovieCard item={item} onClick={onItemClick} />
                <button
                  data-testid="remove-watchlist-btn"
                  className="absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20"
                  style={{ background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,255,255,0.15)' }}
                  onClick={e => { e.stopPropagation(); toggleWatchlistItem(item, false); refresh(); }}
                >
                  <X size={10} color="white" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon={Heart} title="Watchlist empty" subtitle="Start adding movies & shows you want to watch" />
        )}
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Anime Home Page
// ═══════════════════════════════════════════════════
function AnimeHomePage() {
  const navigate = useNavigate();
  const [trending, setTrending] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sq, setSq] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const mobileRef = useRef(null);
  const [cwAnime, setCwAnime] = useState(() => getCW(true));
  const preview = useSearchPreview(sq, true);

  useEffect(() => {
    Promise.all([anilistFetch(Q_TRENDING, { page: 1 }), anilistFetch(Q_POPULAR)])
      .then(([t, p]) => {
        setTrending((t.data?.Page?.media || []).map(mapAnilistItem));
        setPopular((p.data?.Page?.media || []).map(mapAnilistItem));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const onSearch = (e) => { if (e?.preventDefault) e.preventDefault(); if (sq.trim()) navigate(`/anime/search?q=${encodeURIComponent(sq)}`); };
  const onItemClick = (item) => navigate(`/anime/watch/${item.id}`);
  const removeCw = (id) => { const next = getCW(true).filter(i => i.id !== id); saveCW(next, true); setCwAnime(next); };

  return (
    <div style={{ minHeight: '100vh', background: 'rgb(var(--bg))' }} data-testid="anime-home-page">
      <Header sq={sq} setSq={setSq} onSearch={onSearch} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}
        showDrop={showDrop} setShowDrop={setShowDrop} mobileRef={mobileRef} previewResults={preview}
        onItemClick={onItemClick} activeTab="home" isAnime={true} />

      <main className="max-w-[1360px] mx-auto px-4 md:px-6 py-8 md:py-10 pb-20">
        {/* Anime Banner */}
        <div
          className="relative mb-10 rounded-2xl overflow-hidden fade-in"
          style={{
            background: 'linear-gradient(135deg, rgba(var(--anime), 0.1), rgba(var(--anime-pink), 0.06), rgba(var(--bg), 0.95))',
            border: '1px solid rgba(var(--anime), 0.15)',
          }}
          data-testid="anime-banner"
        >
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 80% 50%, rgba(var(--anime), 0.06), transparent 70%)' }} />
          <div className="relative z-10 p-6 md:p-8">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.12em] uppercase mb-3" style={{ color: 'rgb(var(--anime))' }}>
              <Zap size={11} /> Anime Mode
            </div>
            <h1
              className="font-heading font-black italic tracking-tight uppercase leading-none mb-2 text-gradient-anime"
              style={{ fontSize: 'clamp(28px, 5vw, 48px)' }}
            >
              Watch Anime<br />Free & HD
            </h1>
            <p className="text-[13.5px] max-w-md leading-relaxed" style={{ color: 'rgb(var(--muted))' }}>
              Sub & dub available. AnimePahe, VidNest, VidSrc sources. Powered by AniList.
            </p>
          </div>
        </div>

        {/* Continue Watching */}
        {cwAnime.length > 0 && (
          <section className="mb-10" data-testid="anime-continue-watching">
            <SectionHead title="Continue Watching" count={cwAnime.length} isAnime />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {cwAnime.map(item => (
                <div key={item.id} className="relative group">
                  <AnimeCard
                    item={{ ...item, title: item.episodeNumber ? `${item.title} - EP ${item.episodeNumber}` : item.title }}
                    onClick={() => navigate(`/anime/watch/${item.id}?ep=${item.episodeNumber || 1}`)}
                  />
                  <button
                    className="absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20"
                    style={{ background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,255,255,0.15)' }}
                    onClick={e => { e.stopPropagation(); removeCw(item.id); }}
                  >
                    <X size={10} color="white" />
                  </button>
                </div>
              ))}
            </div>
            <div className="my-8" style={{ height: 1, background: 'rgba(var(--border), var(--border-a))' }} />
          </section>
        )}

        {/* Trending */}
        <section className="mb-12" data-testid="anime-trending-section">
          <SectionHead title="Trending Anime" count={!loading ? trending.length : undefined} isAnime />
          {loading ? <GridSkeleton /> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {trending.map(item => <AnimeCard key={item.id} item={item} onClick={onItemClick} />)}
            </div>
          )}
        </section>

        {/* Popular */}
        {!loading && popular.length > 0 && (
          <section data-testid="anime-popular-section">
            <SectionHead title="All-Time Popular" count={popular.length} barColor="rgb(var(--anime-pink))" isAnime />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {popular.map(item => <AnimeCard key={item.id} item={item} onClick={onItemClick} />)}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Anime Search Page
// ═══════════════════════════════════════════════════
function AnimeSearchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [sq, setSq] = useState(query);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const mobileRef = useRef(null);
  const preview = useSearchPreview(sq, true);

  useEffect(() => {
    if (!query) return;
    setLoading(true);
    anilistFetch(Q_SEARCH, { search: query, page: 1 })
      .then(r => setResults((r.data?.Page?.media || []).map(mapAnilistItem)))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [query]);

  const onSearch = (e) => { if (e?.preventDefault) e.preventDefault(); if (sq.trim()) navigate(`/anime/search?q=${encodeURIComponent(sq)}`); };
  const onItemClick = (item) => navigate(`/anime/watch/${item.id}`);

  return (
    <div style={{ minHeight: '100vh', background: 'rgb(var(--bg))' }} data-testid="anime-search-page">
      <Header sq={sq} setSq={setSq} onSearch={onSearch} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}
        showDrop={showDrop} setShowDrop={setShowDrop} mobileRef={mobileRef} previewResults={preview}
        onItemClick={onItemClick} activeTab="search" isAnime={true} />
      <main className="max-w-[1360px] mx-auto px-4 md:px-6 py-8 md:py-10 pb-20">
        <div className="mb-6">
          <h1 className="font-heading font-black italic text-2xl md:text-3xl tracking-tight uppercase leading-none mb-2" style={{ color: 'rgb(var(--fg))' }}>
            Results for <span className="text-gradient-anime">"{query}"</span>
          </h1>
          {!loading && results.length > 0 && <span className="font-mono text-[10px] tracking-wider" style={{ color: 'rgb(var(--muted))' }}>{results.length} anime found</span>}
        </div>
        {loading ? <GridSkeleton /> : results.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {results.map(item => <AnimeCard key={item.id} item={item} onClick={onItemClick} />)}
          </div>
        ) : <EmptyState icon={Zap} title="No anime found" subtitle="Try a different title" isAnime />}
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Anime Watchlist Page
// ═══════════════════════════════════════════════════
function AnimeWatchlistPage() {
  const navigate = useNavigate();
  const [sq, setSq] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const mobileRef = useRef(null);
  const [wl, setWl] = useState(() => getWatchlist(true));
  const preview = useSearchPreview(sq, true);

  const onSearch = (e) => { if (e?.preventDefault) e.preventDefault(); if (sq.trim()) navigate(`/anime/search?q=${encodeURIComponent(sq)}`); };
  const onItemClick = (item) => navigate(`/anime/watch/${item.id}`);
  const refresh = () => setWl(getWatchlist(true));

  return (
    <div style={{ minHeight: '100vh', background: 'rgb(var(--bg))' }} data-testid="anime-watchlist-page">
      <Header sq={sq} setSq={setSq} onSearch={onSearch} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}
        showDrop={showDrop} setShowDrop={setShowDrop} mobileRef={mobileRef} previewResults={preview}
        onItemClick={onItemClick} activeTab="watchlist" isAnime={true} />
      <main className="max-w-[1360px] mx-auto px-4 md:px-6 py-8 md:py-10 pb-20">
        <SectionHead title="Anime Watchlist" count={wl.length} isAnime />
        {wl.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {wl.map(item => (
              <div key={item.id} className="relative group">
                <AnimeCard item={item} onClick={onItemClick} />
                <button
                  className="absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20"
                  style={{ background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,255,255,0.15)' }}
                  onClick={e => { e.stopPropagation(); toggleWatchlistItem(item, true); refresh(); }}
                >
                  <X size={10} color="white" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon={Heart} title="Watchlist empty" subtitle="Start adding anime you want to watch" isAnime />
        )}
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Anime Watch Page
// ═══════════════════════════════════════════════════
function AnimeWatchPage() {
  const { animeId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const epParam = searchParams.get('ep');

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEp, setSelectedEp] = useState(epParam ? parseInt(epParam) : 1);
  const [source, setSource] = useState('animepahe');
  const [dubMode, setDubMode] = useState('sub');
  const [epRange, setEpRange] = useState(0);
  const [epDetails, setEpDetails] = useState({});
  const [epLoading, setEpLoading] = useState(false);
  const [mobileTab, setMobileTab] = useState('player');
  const fetchedPages = useRef(new Set());

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await anilistFetch(Q_DETAILS, { id: parseInt(animeId) });
        const m = res.data?.Media;
        if (!m) { setLoading(false); return; }
        const episodeCount = m.episodes || (m.nextAiringEpisode ? m.nextAiringEpisode.episode - 1 : 12);
        const thumbMap = {};
        (m.streamingEpisodes || []).forEach((se, idx) => { if (se.thumbnail) thumbMap[idx + 1] = se.thumbnail; });
        setDetails({
          id: m.id, idMal: m.idMal,
          title: m.title.english || m.title.romaji,
          titleNative: m.title.native,
          description: (m.description || '').replace(/<[^>]*>/g, '').slice(0, 500),
          image: m.coverImage?.extraLarge || m.coverImage?.large,
          rating: m.averageScore, format: m.format, status: m.status,
          seasonYear: m.seasonYear, genres: m.genres || [],
          studio: m.studios?.nodes?.[0]?.name || null,
          episodeCount, thumbMap,
        });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [animeId]);

  useEffect(() => {
    if (!details || loading) return;
    const cw = getCW(true).filter(i => i.id !== details.id);
    cw.unshift({ id: details.id, title: details.title, image: details.image, format: details.format, releaseDate: details.seasonYear, rating: details.rating, episodeNumber: selectedEp });
    saveCW(cw, true);
  }, [details, selectedEp, loading]);

  useEffect(() => {
    setEpRange(Math.floor((selectedEp - 1) / EP_RANGE_SIZE));
  }, [selectedEp]);

  const fetchJikanPage = useCallback(async (malId, page) => {
    if (!malId || fetchedPages.current.has(page)) return;
    fetchedPages.current.add(page);
    setEpLoading(true);
    try {
      await new Promise(r => setTimeout(r, 300));
      const res = await fetch(`${JIKAN_API}/anime/${malId}/episodes?page=${page}`);
      if (!res.ok) return;
      const data = await res.json();
      const newMap = {};
      (data.data || []).forEach(ep => {
        newMap[ep.mal_id] = {
          title: ep.title_romanji || ep.title || `Episode ${ep.mal_id}`,
          aired: ep.aired ? new Date(ep.aired).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null,
          filler: ep.filler, recap: ep.recap,
        };
      });
      setEpDetails(prev => ({ ...prev, ...newMap }));
    } catch {} finally { setEpLoading(false); }
  }, []);

  useEffect(() => {
    if (!details?.idMal) return;
    const start = epRange * EP_RANGE_SIZE + 1;
    const end = Math.min((epRange + 1) * EP_RANGE_SIZE, details.episodeCount);
    const pageStart = Math.ceil(start / 100);
    const pageEnd = Math.ceil(end / 100);
    for (let p = pageStart; p <= pageEnd; p++) fetchJikanPage(details.idMal, p);
  }, [details, epRange, fetchJikanPage]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
      if (e.key === 'ArrowRight' && details) {
        const next = Math.min(selectedEp + 1, details.episodeCount);
        if (next !== selectedEp) { setSelectedEp(next); setSearchParams({ ep: next }); }
      }
      if (e.key === 'ArrowLeft' && details) {
        const prev = Math.max(selectedEp - 1, 1);
        if (prev !== selectedEp) { setSelectedEp(prev); setSearchParams({ ep: prev }); }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [details, selectedEp, setSearchParams]);

  const handleEpClick = (ep) => {
    setSelectedEp(ep);
    setSearchParams({ ep });
    setMobileTab('player');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: 'rgb(var(--bg))' }}><div className="spinner anime w-9 h-9" /></div>;
  if (!details) return <div className="min-h-screen flex items-center justify-center" style={{ background: 'rgb(var(--bg))' }}><span className="text-base" style={{ color: 'rgb(var(--muted))' }}>Anime not found</span></div>;

  const totalRanges = Math.ceil(details.episodeCount / EP_RANGE_SIZE);
  const ranges = Array.from({ length: totalRanges }, (_, i) => ({ start: i * EP_RANGE_SIZE + 1, end: Math.min((i + 1) * EP_RANGE_SIZE, details.episodeCount), index: i }));
  const currentRangeEps = Array.from({ length: details.episodeCount }, (_, i) => i + 1).slice(epRange * EP_RANGE_SIZE, (epRange + 1) * EP_RANGE_SIZE);
  const currentEpDetail = epDetails[selectedEp] || {};

  const EpisodeList = ({ isMobile }) => (
    <div className={`overflow-y-auto ${isMobile ? 'max-h-[480px]' : 'flex-1'}`}>
      {currentRangeEps.map(epNum => {
        const d = epDetails[epNum] || {};
        const thumb = details.thumbMap?.[epNum] || null;
        const isActive = selectedEp === epNum;
        const showSkel = !d.title && epLoading;
        if (showSkel) return <EpisodeSkeleton key={epNum} />;
        return (
          <button key={epNum} className={`ep-row ${isActive ? 'active-anime' : ''}`} onClick={() => handleEpClick(epNum)} data-testid={`ep-row-${epNum}`}>
            <div className="relative overflow-hidden" style={{ width: 96, aspectRatio: '16/9', background: 'rgba(var(--border), 0.06)', flexShrink: 0 }}>
              {thumb && <img src={thumb} alt={`EP ${epNum}`} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />}
              <div className="absolute inset-0 flex items-center justify-center font-mono font-bold text-base" style={{ color: isActive ? 'rgb(var(--anime))' : 'rgb(var(--muted))' }}>{epNum}</div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" style={{ background: 'rgba(0,0,0,0.4)' }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(var(--anime), 0.9)' }}>
                  <Play size={12} fill="white" color="white" style={{ marginLeft: 1 }} />
                </div>
              </div>
            </div>
            <div className="p-2.5 flex flex-col gap-1 min-w-0 justify-center">
              <div className="flex items-center gap-1.5 font-mono text-[9px] tracking-wider uppercase" style={{ color: 'rgb(var(--muted))' }}>
                EP {epNum}
                {d.filler && <span className="text-[8px] px-1 py-px rounded font-bold" style={{ background: 'rgba(251,191,36,0.1)', color: '#fbbf24' }}>FILLER</span>}
                {d.recap && <span className="text-[8px] px-1 py-px rounded font-bold" style={{ background: 'rgba(var(--anime), 0.1)', color: 'rgb(var(--anime))' }}>RECAP</span>}
              </div>
              <div className="font-semibold text-[12px] leading-tight line-clamp-2" style={{ color: 'rgb(var(--fg))' }}>{d.title || `Episode ${epNum}`}</div>
              {d.aired && <div className="font-mono text-[9px]" style={{ color: 'rgb(var(--muted))' }}>{d.aired}</div>}
            </div>
          </button>
        );
      })}
    </div>
  );

  const Sidebar = () => (
    <aside
      className="hidden lg:flex sticky flex-col rounded-xl overflow-hidden"
      style={{
        top: 'calc(var(--hdr) + 12px)',
        height: 'calc(100vh - var(--hdr) - 24px)',
        background: 'rgb(var(--surface))',
        border: '1px solid rgba(var(--anime), 0.14)',
      }}
      data-testid="anime-sidebar"
    >
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ borderBottom: '1px solid rgba(var(--border), var(--border-a))' }}>
        <span className="font-heading font-bold italic text-base tracking-wide uppercase text-gradient-anime">Episodes</span>
        <span className="font-mono text-[9px] px-2 py-0.5 rounded" style={{ color: 'rgb(var(--muted))', background: 'rgba(var(--border), 0.06)', border: '1px solid rgba(var(--border), var(--border-a))' }}>{details.episodeCount} eps</span>
      </div>
      {ranges.length > 1 && (
        <div className="px-3 py-2 flex-shrink-0" style={{ borderBottom: '1px solid rgba(var(--border), var(--border-a))' }}>
          <div className="relative">
            <select
              className="w-full h-8 pl-2.5 pr-7 rounded-lg font-mono text-[10px] tracking-wider cursor-pointer outline-none"
              style={{ background: 'rgba(var(--border), 0.05)', border: '1px solid rgba(var(--border), var(--border-a))', color: 'rgb(var(--fg))' }}
              value={epRange}
              onChange={e => setEpRange(parseInt(e.target.value))}
            >
              {ranges.map(r => <option key={r.index} value={r.index}>Ep {r.start}-{r.end}{selectedEp >= r.start && selectedEp <= r.end ? ' > now' : ''}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgb(var(--muted))' }} />
          </div>
        </div>
      )}
      <div className="px-4 py-1.5 flex items-center justify-between flex-shrink-0 font-mono text-[9px] tracking-wider uppercase" style={{ color: 'rgb(var(--muted))' }}>
        <span>Showing {currentRangeEps[0]}-{currentRangeEps[currentRangeEps.length - 1]}</span>
        {epLoading && <span style={{ color: 'rgb(var(--anime))' }}>loading...</span>}
      </div>
      <EpisodeList />
    </aside>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'rgb(var(--bg))' }} data-testid="anime-watch-page">
      <main className="max-w-[1360px] mx-auto px-4 md:px-6 py-6 pb-20">
        <button
          data-testid="back-btn"
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 mb-5"
          style={{ background: 'rgba(var(--border), 0.06)', border: '1px solid rgba(var(--border), var(--border-a))', color: 'rgb(var(--muted))' }}
          onClick={() => navigate('/anime')}
        >
          <ArrowLeft size={14} /> Back to Anime
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] xl:grid-cols-[1fr_380px] gap-5 items-start">
          <div className="min-w-0">
            {/* Toolbar */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="flex items-center rounded-lg p-0.5 gap-0.5" style={{ background: 'rgba(var(--border), 0.06)', border: '1px solid rgba(var(--border), var(--border-a))' }}>
                <button data-testid="sub-btn" className={`px-2.5 py-1 rounded-md font-mono text-[10px] font-bold tracking-wider uppercase transition-all ${dubMode === 'sub' ? '' : ''}`}
                  style={{ background: dubMode === 'sub' ? 'rgba(var(--anime), 0.2)' : 'transparent', color: dubMode === 'sub' ? 'rgb(var(--anime))' : 'rgb(var(--muted))' }}
                  onClick={() => setDubMode('sub')}>SUB</button>
                <button data-testid="dub-btn" className={`px-2.5 py-1 rounded-md font-mono text-[10px] font-bold tracking-wider uppercase transition-all`}
                  style={{ background: dubMode === 'dub' ? 'rgba(var(--anime), 0.2)' : 'transparent', color: dubMode === 'dub' ? 'rgb(var(--anime))' : 'rgb(var(--muted))' }}
                  onClick={() => setDubMode('dub')}>DUB</button>
              </div>
              <div className="relative">
                <select
                  data-testid="anime-source-select"
                  value={source}
                  onChange={e => setSource(e.target.value)}
                  className="h-8 pl-3 pr-7 rounded-lg font-body text-[13px] cursor-pointer outline-none min-w-[170px]"
                  style={{ background: 'rgba(var(--border), 0.06)', border: '1px solid rgba(var(--border), var(--border-a))', color: 'rgb(var(--fg))' }}
                >
                  <option value="animepahe">AnimePahe (VidNest)</option>
                  <option value="vidnest">VidNest</option>
                  <option value="vidsrc-cc">VidSrc.cc</option>
                  <option value="vidsrc-icu">VidSrc.icu</option>
                  <option value="anibhindi">AniBiee (Hindi)</option>
                </select>
                <ChevronDown size={13} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgb(var(--muted))' }} />
              </div>
            </div>

            {/* Player */}
            <div className="player-frame" style={{ borderColor: 'rgba(var(--anime), 0.16)' }} data-testid="anime-player-frame">
              <AnimePlayer animeId={details.id} episode={selectedEp} source={source} dubMode={dubMode} animeTitle={details.title} />
            </div>

            {/* Now Playing */}
            <div className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg mt-3.5 mb-3" style={{ background: 'rgba(var(--border), 0.04)', border: '1px solid rgba(var(--border), var(--border-a))' }}>
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'rgb(var(--anime))', boxShadow: '0 0 8px rgb(var(--anime))', animation: 'np-glow 2s ease-in-out infinite' }} />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[9px] tracking-wider uppercase mb-0.5" style={{ color: 'rgb(var(--muted))' }}>Now Playing</div>
                <div className="font-semibold text-[13px] truncate" style={{ color: 'rgb(var(--fg))' }}>
                  Episode {selectedEp}{currentEpDetail.title ? ` - ${currentEpDetail.title}` : ''}
                </div>
              </div>
            </div>

            {/* Mobile episode tabs */}
            {details.episodeCount > 0 && (
              <div className="flex lg:hidden" style={{ borderBottom: '1px solid rgba(var(--border), var(--border-a))' }}>
                <button
                  className={`flex-1 py-3 font-mono text-[10px] font-bold tracking-wider uppercase transition-all`}
                  style={{
                    color: mobileTab === 'player' ? 'rgb(var(--fg))' : 'rgb(var(--muted))',
                    borderBottom: `2px solid ${mobileTab === 'player' ? 'rgb(var(--anime))' : 'transparent'}`,
                  }}
                  onClick={() => setMobileTab('player')}
                >About</button>
                <button
                  className={`flex-1 py-3 font-mono text-[10px] font-bold tracking-wider uppercase transition-all`}
                  style={{
                    color: mobileTab === 'episodes' ? 'rgb(var(--fg))' : 'rgb(var(--muted))',
                    borderBottom: `2px solid ${mobileTab === 'episodes' ? 'rgb(var(--anime))' : 'transparent'}`,
                  }}
                  onClick={() => setMobileTab('episodes')}
                >Episodes ({details.episodeCount})</button>
              </div>
            )}

            {/* Mobile Episodes */}
            {mobileTab === 'episodes' && (
              <div className="lg:hidden mt-4 rounded-xl overflow-hidden" style={{ background: 'rgb(var(--surface))', border: '1px solid rgba(var(--anime), 0.14)' }}>
                <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(var(--border), var(--border-a))' }}>
                  <span className="font-heading font-bold italic text-base tracking-wide uppercase text-gradient-anime">Episodes</span>
                  {ranges.length > 1 && (
                    <div className="relative" style={{ width: 140 }}>
                      <select className="w-full h-7 pl-2 pr-6 rounded font-mono text-[10px] cursor-pointer outline-none"
                        style={{ background: 'rgba(var(--border), 0.05)', border: '1px solid rgba(var(--border), var(--border-a))', color: 'rgb(var(--fg))' }}
                        value={epRange} onChange={e => setEpRange(parseInt(e.target.value))}>
                        {ranges.map(r => <option key={r.index} value={r.index}>Ep {r.start}-{r.end}</option>)}
                      </select>
                      <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgb(var(--muted))' }} />
                    </div>
                  )}
                </div>
                <EpisodeList isMobile />
              </div>
            )}

            {/* About section */}
            {mobileTab === 'player' && (
              <div className="mt-4">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2 py-1 rounded font-mono text-[9px] font-bold tracking-wider uppercase"
                    style={{ background: 'rgba(var(--anime), 0.1)', color: 'rgb(var(--anime))' }}>
                    <Zap size={10} className="inline mr-1" />{details.format?.replace(/_/g, ' ') || 'Anime'}
                  </span>
                  {details.seasonYear && <span className="flex items-center gap-1 text-[13px]" style={{ color: 'rgb(var(--muted))' }}><Calendar size={13} />{details.seasonYear}</span>}
                  {details.episodeCount && <span className="flex items-center gap-1 text-[13px]" style={{ color: 'rgb(var(--muted))' }}><Tv size={13} />{details.episodeCount} eps</span>}
                  {details.rating && <span className="flex items-center gap-1 text-[13px]" style={{ color: '#fbbf24' }}><Star size={13} />{(details.rating / 10).toFixed(1)}</span>}
                </div>
                <h1 className="font-heading font-black italic tracking-tight uppercase leading-none mb-3 text-gradient-anime" style={{ fontSize: 'clamp(24px, 4vw, 36px)' }}>
                  {details.title}
                </h1>
                {details.studio && (
                  <div className="flex items-center gap-1.5 text-[12px] mb-3" style={{ color: 'rgb(var(--muted))' }}>
                    <Flame size={12} /><span className="font-semibold" style={{ color: 'rgb(var(--anime))' }}>{details.studio}</span>
                  </div>
                )}
                {details.description && <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'rgb(var(--muted))' }}>{details.description}{details.description.length >= 499 ? '...' : ''}</p>}
                {details.genres.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {details.genres.map((g, i) => (
                      <span key={i} className="px-2.5 py-1 rounded text-[12px]"
                        style={{ background: 'rgba(var(--anime), 0.07)', border: '1px solid rgba(var(--anime), 0.18)', color: 'rgb(var(--anime))' }}>
                        {g}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {details.episodeCount > 0 && <Sidebar />}
        </div>
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Anime Player
// ═══════════════════════════════════════════════════
function AnimePlayer({ animeId, episode, source, dubMode, animeTitle }) {
  const s = { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 };
  const dub = dubMode === 'dub' ? 1 : 0;
  if (source === 'animepahe') return <iframe src={`https://vidnest.fun/animepahe/${animeId}/${episode}/${dubMode}`} allowFullScreen style={s} title="AnimePahe" scrolling="no" />;
  if (source === 'vidnest') return <iframe src={`https://vidnest.fun/anime/${animeId}/${episode}/${dubMode}`} allowFullScreen style={s} title="VidNest" scrolling="no" />;
  if (source === 'vidsrc-cc') return <iframe src={`https://vidsrc.cc/v2/embed/anime/${animeId}/${episode}/${dubMode}`} allowFullScreen style={s} title="VidSrc.cc" />;
  if (source === 'vidsrc-icu') return <iframe src={`https://vidsrc.icu/embed/anime/${animeId}/${episode}/${dub}`} allowFullScreen style={s} title="VidSrc.icu" />;
  if (source === 'anibhindi') return <AniBieePlayer animeTitle={animeTitle} episode={episode} iframeStyle={s} />;
  return <div className="absolute inset-0 flex items-center justify-center text-[14px]" style={{ color: 'rgb(var(--muted))' }}>Source unavailable</div>;
}

function AniBieePlayer({ animeTitle, episode, iframeStyle }) {
  const [embedUrl, setEmbedUrl] = useState(null);
  const [status, setStatus] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!animeTitle) { setStatus('error'); setErrorMsg('No title available'); return; }
    let cancelled = false;
    setStatus('loading'); setEmbedUrl(null);
    const run = async () => {
      try {
        const searchRes = await fetch(`${ANIBHINDI_API}/api/search?q=${encodeURIComponent(animeTitle)}`);
        if (!searchRes.ok) throw new Error(`Search failed (${searchRes.status})`);
        const searchData = await searchRes.json();
        const items = searchData?.data?.items || [];
        if (!items.length) throw new Error(`"${animeTitle}" not found on AniBiee`);
        const anibeeId = items[0].id;
        const episodeId = `${anibeeId}-1x${episode}`;
        const embedRes = await fetch(`${ANIBHINDI_API}/api/embed/${episodeId}`);
        if (!embedRes.ok) throw new Error(`Embed not found for episode ${episode}`);
        const embedData = await embedRes.json();
        const servers = embedData?.data?.servers || embedData?.servers || [];
        if (!servers.length) throw new Error('No servers available');
        const url = servers[0].url;
        if (!url) throw new Error('Invalid embed URL');
        if (!cancelled) { setEmbedUrl(url); setStatus('ready'); }
      } catch (e) { if (!cancelled) { setStatus('error'); setErrorMsg(e.message); } }
    };
    run();
    return () => { cancelled = true; };
  }, [animeTitle, episode]);

  if (status === 'loading') return <PlayerLoading label="Loading Hindi source..." color="var(--anime)" />;
  if (status === 'error') return <PlayerError title="Hindi source unavailable" message={errorMsg} color="var(--anime)" />;
  return <iframe src={embedUrl} allowFullScreen style={iframeStyle} title="AniBiee Hindi" scrolling="no" />;
}

// ═══════════════════════════════════════════════════
// Player Loading & Error (reusable)
// ═══════════════════════════════════════════════════
function PlayerLoading({ label, color }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3.5 bg-black">
      <div className="relative w-11 h-11">
        <div className="absolute inset-0 rounded-full" style={{ border: `2px solid rgba(${color === 'var(--anime)' ? 'var(--anime)' : 'var(--crimson)'}, 0.12)` }} />
        <div className="absolute inset-0 rounded-full spinner" style={{ borderTopColor: `rgb(${color === 'var(--anime)' ? 'var(--anime)' : 'var(--crimson)'})` }} />
      </div>
      <div className="text-center">
        <div className="font-mono text-[9px] tracking-[0.12em] uppercase mb-1" style={{ color: `rgb(${color === 'var(--anime)' ? 'var(--anime)' : 'var(--crimson)'})` }}>{label}</div>
      </div>
    </div>
  );
}

function PlayerError({ title, message, color }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 bg-black text-center px-7">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-1"
        style={{ background: `rgba(${color === 'var(--anime)' ? 'var(--anime)' : 'var(--crimson)'}, 0.08)`, border: `1px solid rgba(${color === 'var(--anime)' ? 'var(--anime)' : 'var(--crimson)'}, 0.18)` }}>
        <Film size={20} style={{ color: `rgb(${color === 'var(--anime)' ? 'var(--anime)' : 'var(--crimson)'})` }} />
      </div>
      <span className="font-heading font-bold italic text-lg tracking-wide uppercase" style={{ color: '#666' }}>{title}</span>
      <span className="font-mono text-[9px] max-w-[280px] leading-relaxed" style={{ color: '#444' }}>{message}</span>
      <span className="font-mono text-[8px] mt-1" style={{ color: '#2a2a2a' }}>Try a different source from the dropdown</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// PovScraper Player
// ═══════════════════════════════════════════════════
function PovScraperPlayer({ title, isTV, season, episode }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [phase, setPhase] = useState('searching');
  const [errorMsg, setErrorMsg] = useState('');
  const [slug, setSlug] = useState(null);
  const [subjectId, setSubjectId] = useState(null);
  const [dubs, setDubs] = useState([]);
  const [selectedDubIdx, setSelectedDubIdx] = useState(0);
  const [streams, setStreams] = useState([]);
  const [activeUrl, setActiveUrl] = useState(null);

  useEffect(() => {
    if (!title) return;
    let cancelled = false;
    setPhase('searching'); setSlug(null); setDubs([]); setStreams([]); setActiveUrl(null); setSelectedDubIdx(0); setSubjectId(null);
    const run = async () => {
      try {
        const r = await fetch(`${POVSCRAPER}/search?q=${encodeURIComponent(title)}`);
        if (!r.ok) throw new Error(`Search API returned ${r.status}`);
        const d = await r.json();
        const results = d.movies || [];
        if (!results.length) throw new Error(`"${title}" not found`);
        const titleLower = title.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
        const LANG_TAGS = /\[(hindi|tamil|telugu|dubbed|malayalam|kannada|bengali|punjabi|urdu|ost|soundtrack|music)\]/i;
        const scored = results.map(m => {
          const rawName = m.name || '';
          const name = rawName.toLowerCase().replace(/\s*\[.*?\]/g, '').replace(/[^a-z0-9\s]/g, '').trim();
          let score = name === titleLower ? 100 : name.includes(titleLower) || titleLower.includes(name) ? 50 : 0;
          if (score === 0) return { ...m, score };
          if (LANG_TAGS.test(rawName)) score -= 40;
          if (name.length > titleLower.length + 4) score -= 20;
          return { ...m, score };
        }).sort((a, b) => b.score - a.score);
        if (!cancelled) setSlug(scored[0].slug);
      } catch (e) { if (!cancelled) { setPhase('error'); setErrorMsg(e.message); } }
    };
    run();
    return () => { cancelled = true; };
  }, [title]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false; setPhase('resolving');
    const run = async () => {
      try {
        const r = await fetch(`${POVSCRAPER}/detail/${slug}`);
        if (!r.ok) throw new Error(`Detail returned ${r.status}`);
        const d = await r.json();
        const meta = d.metadata || {};
        if (!cancelled) { setSubjectId(meta.id); setDubs(meta.dubs || []); setSelectedDubIdx(0); }
      } catch (e) { if (!cancelled) { setPhase('error'); setErrorMsg(e.message); } }
    };
    run();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!subjectId && !dubs.length) return;
    const dub = dubs[selectedDubIdx];
    const sid = dub ? dub.subjectId : subjectId;
    const dp = dub ? dub.detailPath : slug;
    if (!sid || !dp) return;
    let cancelled = false; setPhase('fetching');
    const run = async () => {
      try {
        const se = isTV ? season : 0; const ep = isTV ? episode : 0;
        const url = `${POVSCRAPER}/api/stream/${sid}?detail_path=${encodeURIComponent(dp)}&se=${se}&ep=${ep}`;
        const r = await fetch(url);
        if (!r.ok) throw new Error(`Stream API returned ${r.status}`);
        const d = await r.json();
        if (d.detail) throw new Error(typeof d.detail === 'object' ? (d.detail.message || JSON.stringify(d.detail)) : d.detail);
        const sources = d.sources || [];
        if (!sources.length) throw new Error('No streams available');
        if (!cancelled) {
          setStreams(sources);
          setActiveUrl(`${POVSCRAPER}/proxy/stream?url=${encodeURIComponent(sources[0].url)}`);
          setPhase('ready');
        }
      } catch (e) { if (!cancelled) { setPhase('error'); setErrorMsg(e.message); } }
    };
    run();
    return () => { cancelled = true; };
  }, [dubs, selectedDubIdx, subjectId, slug, isTV, season, episode]);

  useEffect(() => {
    if (phase !== 'ready' || !activeUrl || !videoRef.current) return;
    const video = videoRef.current;
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    const isHls = activeUrl.includes('.m3u8') || activeUrl.includes('/m3u8/');
    const setup = () => {
      if (isHls && window.Hls?.isSupported()) {
        const hls = new window.Hls({ maxBufferLength: 30, maxMaxBufferLength: 120 });
        hls.loadSource(activeUrl); hls.attachMedia(video);
        hls.on(window.Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = activeUrl; video.play().catch(() => {});
      } else { video.src = activeUrl; video.play().catch(() => {}); }
    };
    if (isHls && !window.Hls) {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/hls.js@1.4.10/dist/hls.min.js';
      s.onload = setup; document.head.appendChild(s);
    } else setup();
    return () => { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } };
  }, [phase, activeUrl]);

  const labels = { searching: 'Searching MovieBox...', resolving: 'Loading metadata...', fetching: 'Fetching stream...' };
  if (phase !== 'ready' && phase !== 'error') return <PlayerLoading label={labels[phase] || 'Loading...'} color="var(--crimson)" />;
  if (phase === 'error') return <PlayerError title="MovieBox Unavailable" message={errorMsg} color="var(--crimson)" />;

  const selStyle = "h-7 px-2 rounded font-mono text-[10px] cursor-pointer outline-none max-w-[180px]";
  return (
    <div className="absolute inset-0 bg-black">
      <video ref={videoRef} className="w-full h-full" controls playsInline crossOrigin="anonymous" />
      <div className="absolute top-2 right-2 flex gap-1.5 z-10 flex-wrap justify-end">
        {streams.length > 1 && (
          <select value={activeUrl} onChange={e => setActiveUrl(e.target.value)}
            className={selStyle} style={{ background: 'rgba(6,6,10,0.9)', border: '1px solid rgba(249,115,22,0.25)', color: '#f0ece4' }}>
            {streams.map((s, i) => <option key={i} value={s.url}>{s.resolution && s.resolution !== 'Unknown' ? s.resolution : s.format || `Stream ${i + 1}`}</option>)}
          </select>
        )}
        {dubs.length > 1 && (
          <select value={selectedDubIdx} onChange={e => setSelectedDubIdx(parseInt(e.target.value))}
            className={selStyle} style={{ background: 'rgba(6,6,10,0.9)', border: '1px solid rgba(249,115,22,0.25)', color: '#f0ece4' }}>
            {dubs.map((d, i) => <option key={i} value={i}>{d.lanName || d.lanCode || `Audio ${i + 1}`}</option>)}
          </select>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// Movies/TV Watch Page
// ═══════════════════════════════════════════════════
function WatchPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const seasonParam = searchParams.get('season');
  const episodeIdParam = searchParams.get('episodeId');

  const [itemDetails, setItemDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [tmdbId, setTmdbId] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(seasonParam ? parseInt(seasonParam) : 1);
  const [playerSource, setPlayerSource] = useState('adless');
  const [fetchedEpImages, setFetchedEpImages] = useState({});
  const [mobileTab, setMobileTab] = useState('player');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (itemId) fetchItemDetails(itemId); }, [itemId]);
  useEffect(() => { if (itemDetails && episodeIdParam) { const ep = itemDetails.episodes?.find(e => e.id === episodeIdParam); if (ep) setSelectedEpisode(ep); } }, [itemDetails, episodeIdParam]);

  const buildEpisodesFromTmdb = async (tid, seasonsArr) => {
    const all = await Promise.all(
      seasonsArr.map(s =>
        fetchWithRetry(`${TMDB_API_BASE}/tv/${tid}/season/${s}?api_key=${TMDB_API_KEY}`)
          .then(r => r.json())
          .then(sd => (sd.episodes || []).map(ep => ({ id: `${tid}-s${s}e${ep.episode_number}`, title: ep.name, number: ep.episode_number, season: s, seasonNumber: s, description: ep.overview, image: ep.still_path ? `https://image.tmdb.org/t/p/w300${ep.still_path}` : null })))
          .catch(() => [])
      )
    );
    return all.flat();
  };

  const fetchItemDetails = async (id) => {
    try {
      setLoading(true);
      const isImdb = /^tt\d+$/.test(id);
      const isTmdbMovie = id.startsWith('tmdb-movie-');
      const isTmdbTv = id.startsWith('tmdb-tv-');

      if (isTmdbMovie || isTmdbTv) {
        const cacheKey = id;
        if (tmdbDetailsCache.has(cacheKey)) {
          const cached = tmdbDetailsCache.get(cacheKey);
          setItemDetails(cached.details); setTmdbId(cached.tid); setLoading(false);
          saveMovieCW({ id, title: cached.details.title, image: cached.details.image, type: cached.details.type, releaseDate: cached.details.releaseDate, isImdbId: !!cached.details.imdbId }, null, seasonParam ? parseInt(seasonParam) : 1);
          return;
        }
        const tid = parseInt(id.replace(isTmdbMovie ? 'tmdb-movie-' : 'tmdb-tv-', ''));
        const type = isTmdbMovie ? 'Movie' : 'TV Series';
        const endpoint = isTmdbMovie ? 'movie' : 'tv';
        const [detailsData, extIds] = await Promise.all([
          fetchWithRetry(`${TMDB_API_BASE}/${endpoint}/${tid}?api_key=${TMDB_API_KEY}`).then(r => r.json()),
          fetchWithRetry(`${TMDB_API_BASE}/${endpoint}/${tid}/external_ids?api_key=${TMDB_API_KEY}`).then(r => r.json()).catch(() => ({})),
        ]);
        const imdbId = extIds.imdb_id || null;
        let episodes = [];
        if (type === 'TV Series') {
          const sns = (detailsData.seasons || []).filter(s => s.season_number > 0).map(s => s.season_number);
          episodes = await buildEpisodesFromTmdb(tid, sns.length ? sns : [1]);
        }
        setTmdbId(tid);
        const built = { id, title: detailsData.title || detailsData.name, type, releaseDate: detailsData.release_date || detailsData.first_air_date, description: detailsData.overview, genres: detailsData.genres?.map(g => g.name) || [], image: detailsData.poster_path ? `https://image.tmdb.org/t/p/w500${detailsData.poster_path}` : null, episodes, imdbId };
        tmdbDetailsCache.set(cacheKey, { details: built, tid });
        setItemDetails(built);
        saveMovieCW({ id, title: built.title, image: built.image, type, releaseDate: built.releaseDate, isImdbId: !!imdbId }, null, seasonParam ? parseInt(seasonParam) : 1);
        return;
      }

      if (isImdb) {
        const findData = await fetchWithRetry(`${TMDB_API_BASE}/find/${id}?api_key=${TMDB_API_KEY}&external_source=imdb_id`).then(r => r.json());
        const movie = findData.movie_results?.[0]; const tv = findData.tv_results?.[0]; const item = movie || tv;
        if (!item) { setLoading(false); return; }
        const tid = item.id; const type = movie ? 'Movie' : 'TV Series'; const endpoint = movie ? 'movie' : 'tv';
        const detailsData = await fetchWithRetry(`${TMDB_API_BASE}/${endpoint}/${tid}?api_key=${TMDB_API_KEY}`).then(r => r.json());
        let episodes = [];
        if (type === 'TV Series') { const sns = (detailsData.seasons || []).filter(s => s.season_number > 0).map(s => s.season_number); episodes = await buildEpisodesFromTmdb(tid, sns.length ? sns : [1]); }
        setTmdbId(tid);
        const built = { id, title: item.title || item.name, type, releaseDate: item.release_date || item.first_air_date, description: item.overview, genres: detailsData.genres?.map(g => g.name) || [], image: item.poster_path ? `https://image.tmdb.org/t/p/w500${item.poster_path}` : null, episodes, imdbId: id };
        setItemDetails(built);
        saveMovieCW({ id, title: built.title, image: built.image, type, releaseDate: built.releaseDate, isImdbId: true }, null, seasonParam ? parseInt(seasonParam) : 1);
        return;
      }

      const data = await fetch(`${API_BASE}/info?id=${encodeURIComponent(id)}`).then(r => r.json());
      setItemDetails(data);
      const ep = (data.type === 'TV Series' || data.type === 'TV') ? 'tv' : 'movie';
      fetchWithRetry(`${TMDB_API_BASE}/search/${ep}?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(data.title)}${data.releaseDate ? `&year=${data.releaseDate.slice(0, 4)}` : ''}`).then(r => r.json()).then(d => { if (d?.results?.length) setTmdbId(d.results[0].id); }).catch(() => {});
      saveMovieCW({ id, title: data.title, image: data.image, type: data.type, releaseDate: data.releaseDate, isImdbId: false }, null, seasonParam ? parseInt(seasonParam) : 1);
    } catch { setItemDetails(null); } finally { setLoading(false); }
  };

  const saveMovieCW = (item, episode, season = 1) => {
    const isTV = item.type === 'TV Series' || item.type === 'TV';
    const cw = getCW(false).filter(i => i.id !== item.id);
    cw.unshift({ ...item, season: isTV ? season : undefined, episodeId: isTV ? (episode?.id || null) : undefined, episodeTitle: isTV ? (episode?.title || null) : undefined, episodeNumber: isTV ? (episode?.number || episode?.episode || undefined) : undefined });
    saveCW(cw, false);
  };

  const handleEpisodeClick = (ep) => {
    setSelectedEpisode(ep);
    const p = new URLSearchParams(searchParams); p.set('season', selectedSeason); p.set('episodeId', ep.id);
    navigate(`/watch/${itemId}?${p}`, { replace: true });
    saveMovieCW({ id: itemId, title: itemDetails.title, image: itemDetails.image, type: itemDetails.type, releaseDate: itemDetails.releaseDate, isImdbId: /^tt\d+$/.test(itemId) }, ep, selectedSeason);
    setMobileTab('player');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSeasonChange = (s) => { setSelectedSeason(s); setSelectedEpisode(null); navigate(`/watch/${itemId}?season=${s}`, { replace: true }); };
  const getEpsForSeason = (s) => (itemDetails?.episodes || []).filter(ep => (ep.seasonNumber || ep.season || 1) === s);
  const getSeasons = () => { if (!itemDetails?.episodes) return [1]; return [...new Set(itemDetails.episodes.map(ep => ep.seasonNumber || ep.season || 1))].sort((a, b) => a - b); };
  const getImdbId = () => itemDetails ? (itemDetails.imdbId || itemDetails.imdbID || itemDetails.imdb || null) : null;
  const isTV = itemDetails?.type === 'TV Series' || itemDetails?.type === 'TV';
  const getSE = () => {
    if (selectedEpisode) return { season: selectedEpisode.seasonNumber || selectedEpisode.season || 1, episode: selectedEpisode.number || selectedEpisode.episode || 1 };
    if (itemDetails?.episodes?.length) return { season: itemDetails.episodes[0].seasonNumber || 1, episode: itemDetails.episodes[0].number || 1 };
    return { season: 1, episode: 1 };
  };

  const getPlayerSrc = () => {
    const imdbId = getImdbId();
    const vId = imdbId?.startsWith('tt') ? imdbId : tmdbId;
    const safeId = imdbId?.startsWith('tt') ? imdbId : tmdbId;
    const { season, episode } = getSE();
    const isM = !isTV;
    switch (playerSource) {
      case 'adless': return isM ? `${EMBEDMASTER}/ajvbxpervogyvmz1/movie/${vId}` : `${EMBEDMASTER}/ajvbxpervogyvmz1/tv/${vId}/${season}/${episode}`;
      case 'vidsrc.sbs': return isM ? `https://vidsrc.sbs/embed/movie/${tmdbId}` : `https://vidsrc.sbs/embed/tv/${tmdbId}/${season}/${episode}`;
      case 'vidfast': return isM ? `https://vidfast.vc/movie/${vId}?autoPlay=true` : `https://vidfast.vc/tv/${vId}/${season}/${episode}?autoPlay=true`;
      case 'videasy': return isM ? `https://player.videasy.net/movie/${tmdbId}` : `https://player.videasy.net/tv/${tmdbId}/${season}/${episode}`;
      case 'vidrock': return isM ? `https://vidrock.net/movie/${safeId}?download=false` : `https://vidrock.net/tv/${safeId}/${season}/${episode}?download=false`;
      default: return isM ? `${EMBEDMASTER}/ajvbxpervogyvmz1/movie/${vId}` : `${EMBEDMASTER}/ajvbxpervogyvmz1/tv/${vId}/${season}/${episode}`;
    }
  };

  const fetchEpImage = async (tid, season, ep, key) => {
    if (!tid || fetchedEpImages[key] !== undefined) return;
    setFetchedEpImages(p => ({ ...p, [key]: null }));
    try {
      const d = await fetchWithRetry(`${TMDB_API_BASE}/tv/${tid}/season/${season}/episode/${ep}?api_key=${TMDB_API_KEY}`).then(r => r.json());
      setFetchedEpImages(p => ({ ...p, [key]: d?.still_path ? `https://image.tmdb.org/t/p/w300${d.still_path}` : null }));
    } catch {}
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
      if (!isTV || !itemDetails?.episodes?.length) return;
      const eps = getEpsForSeason(selectedSeason);
      const curIdx = eps.findIndex(ep => ep.id === selectedEpisode?.id);
      if (e.key === 'ArrowRight' && curIdx < eps.length - 1) handleEpisodeClick(eps[curIdx + 1]);
      if (e.key === 'ArrowLeft' && curIdx > 0) handleEpisodeClick(eps[curIdx - 1]);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: 'rgb(var(--bg))' }}><div className="spinner w-9 h-9" /></div>;
  if (!itemDetails) return <div className="min-h-screen flex items-center justify-center" style={{ background: 'rgb(var(--bg))' }}><span className="text-base" style={{ color: 'rgb(var(--muted))' }}>Content not found</span></div>;

  const hasSidebar = isTV && itemDetails.episodes?.length > 0;
  const { season: curSeason, episode: curEpisode } = getSE();
  const currentEpTitle = selectedEpisode?.title || (hasSidebar ? getEpsForSeason(selectedSeason)[0]?.title : null);
  const episodesForSeason = getEpsForSeason(selectedSeason);
  const isDirectPlayer = playerSource === 'povmovie';

  const EpisodeRow = ({ ep, idx }) => {
    const epNum = ep.number || ep.episode || idx + 1;
    const key = `${itemDetails.id}_${selectedSeason}_${epNum}`;
    let img = ep.image || ep.img;
    if (!img && tmdbId) { if (fetchedEpImages[key] === undefined) fetchEpImage(tmdbId, selectedSeason, epNum, key); img = fetchedEpImages[key]; }
    const isActive = selectedEpisode?.id === ep.id;
    return (
      <button key={ep.id || idx} onClick={() => handleEpisodeClick(ep)} className={`ep-row ${isActive ? 'active-tv' : ''}`} data-testid={`ep-row-${epNum}`}>
        <div className="relative overflow-hidden" style={{ width: 96, aspectRatio: '16/9', background: 'rgba(var(--border), 0.06)', flexShrink: 0 }}>
          {img && <img src={img} alt={ep.title} className="w-full h-full object-cover" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />}
          <div className="absolute inset-0 flex items-center justify-center font-mono font-bold text-base" style={{ color: isActive ? 'rgb(var(--crimson))' : 'rgb(var(--muted))' }}>{epNum}</div>
          <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" style={{ background: 'rgba(0,0,0,0.4)' }}>
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(var(--crimson), 0.9)' }}>
              <Play size={12} fill="white" color="white" style={{ marginLeft: 1 }} />
            </div>
          </div>
        </div>
        <div className="p-2.5 flex flex-col gap-1 min-w-0 justify-center">
          <div className="font-mono text-[9px] tracking-wider uppercase" style={{ color: 'rgb(var(--muted))' }}>EP {epNum}</div>
          <div className="font-semibold text-[12px] leading-tight line-clamp-2" style={{ color: 'rgb(var(--fg))' }}>{ep.title || `Episode ${epNum}`}</div>
          {ep.description && <div className="text-[11px] leading-snug line-clamp-2" style={{ color: 'rgb(var(--muted))' }}>{ep.description}</div>}
        </div>
      </button>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: 'rgb(var(--bg))' }} data-testid="watch-page">
      <main className="max-w-[1360px] mx-auto px-4 md:px-6 py-6 pb-20">
        <button data-testid="back-btn" className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 mb-5"
          style={{ background: 'rgba(var(--border), 0.06)', border: '1px solid rgba(var(--border), var(--border-a))', color: 'rgb(var(--muted))' }}
          onClick={() => navigate('/')}>
          <ArrowLeft size={14} /> Back to Home
        </button>

        <div className={`grid gap-5 items-start ${hasSidebar ? 'grid-cols-1 lg:grid-cols-[1fr_340px] xl:grid-cols-[1fr_380px]' : 'grid-cols-1'}`}>
          <div className="min-w-0">
            {/* Toolbar */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {tmdbId && (
                <a href={isTV ? `https://vidvault.ru/tv/${getImdbId()?.startsWith('tt') ? getImdbId() : tmdbId}/${curSeason}/${curEpisode}` : `https://vidvault.ru/movie/${getImdbId()?.startsWith('tt') ? getImdbId() : tmdbId}`}
                  target="_blank" rel="noopener noreferrer" data-testid="download-btn"
                  className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-medium transition-all duration-200"
                  style={{ background: 'rgba(var(--border), 0.06)', border: '1px solid rgba(var(--border), var(--border-a))', color: 'rgb(var(--muted))' }}>
                  <Download size={14} /><span className="hidden sm:inline">Download</span>
                </a>
              )}
              <div className="relative">
                <select data-testid="source-select" value={playerSource} onChange={e => setPlayerSource(e.target.value)}
                  className="h-8 pl-3 pr-7 rounded-lg font-body text-[13px] cursor-pointer outline-none min-w-[170px]"
                  style={{ background: 'rgba(var(--border), 0.06)', border: '1px solid rgba(var(--border), var(--border-a))', color: 'rgb(var(--fg))' }}>
                  <option value="adless">povertymovie (Adless)</option>
                  <option value="vidsrc.sbs">VidSrc.sbs (+Hin)</option>
                  <option value="vidfast">VidFast.vc</option>
                  <option value="videasy">Videasy.net</option>
                  <option value="povmovie">Mvbox (Adless+Hin)</option>
                  <option value="vidrock">VidRock.net</option>
                </select>
                <ChevronDown size={13} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgb(var(--muted))' }} />
              </div>
            </div>

            {/* Player */}
            <div className="player-frame" data-testid="player-frame">
              {playerSource === 'povmovie' && <PovScraperPlayer title={itemDetails.title} isTV={isTV} season={curSeason} episode={curEpisode} />}
              {!isDirectPlayer && (!tmdbId && !getImdbId()
                ? <div className="flex items-center justify-center h-full text-[14px]" style={{ color: 'rgb(var(--muted))' }}>ID not found</div>
                : <iframe src={getPlayerSrc()} allowFullScreen allow="autoplay; fullscreen *; picture-in-picture; encrypted-media" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} title="Player" scrolling="no" />
              )}
            </div>

            {/* Now Playing */}
            {isTV && (
              <div className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg mt-3.5 mb-3" style={{ background: 'rgba(var(--border), 0.04)', border: '1px solid rgba(var(--border), var(--border-a))' }}>
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'rgb(var(--crimson))', boxShadow: '0 0 8px rgb(var(--crimson))', animation: 'np-glow 2s ease-in-out infinite' }} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[9px] tracking-wider uppercase mb-0.5" style={{ color: 'rgb(var(--muted))' }}>Now Playing - Season {curSeason}</div>
                  <div className="font-semibold text-[13px] truncate" style={{ color: 'rgb(var(--fg))' }}>Episode {curEpisode}{currentEpTitle ? ` - ${currentEpTitle}` : ''}</div>
                </div>
              </div>
            )}

            {/* Mobile tabs */}
            {hasSidebar && (
              <div className="flex lg:hidden" style={{ borderBottom: '1px solid rgba(var(--border), var(--border-a))' }}>
                <button className="flex-1 py-3 font-mono text-[10px] font-bold tracking-wider uppercase" style={{ color: mobileTab === 'player' ? 'rgb(var(--fg))' : 'rgb(var(--muted))', borderBottom: `2px solid ${mobileTab === 'player' ? 'rgb(var(--crimson))' : 'transparent'}` }} onClick={() => setMobileTab('player')}>About</button>
                <button className="flex-1 py-3 font-mono text-[10px] font-bold tracking-wider uppercase" style={{ color: mobileTab === 'episodes' ? 'rgb(var(--fg))' : 'rgb(var(--muted))', borderBottom: `2px solid ${mobileTab === 'episodes' ? 'rgb(var(--crimson))' : 'transparent'}` }} onClick={() => setMobileTab('episodes')}>Episodes ({episodesForSeason.length})</button>
              </div>
            )}

            {/* Mobile Episodes */}
            {hasSidebar && mobileTab === 'episodes' && (
              <div className="lg:hidden mt-4 rounded-xl overflow-hidden" style={{ background: 'rgb(var(--surface))', border: '1px solid rgba(var(--border), var(--border-a))', maxHeight: 480, display: 'flex', flexDirection: 'column' }}>
                <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(var(--border), var(--border-a))' }}>
                  <span className="font-heading font-bold italic text-base tracking-wide uppercase" style={{ color: 'rgb(var(--fg))' }}>Episodes</span>
                  {getSeasons().length > 1 && (
                    <div className="flex gap-1 flex-wrap">
                      {getSeasons().map(s => (
                        <button key={s} onClick={() => handleSeasonChange(s)} className="px-2.5 py-1 rounded-full font-mono text-[9px] font-bold tracking-wider uppercase transition-all"
                          style={{ background: selectedSeason === s ? 'rgb(var(--crimson))' : 'rgba(var(--border), 0.06)', color: selectedSeason === s ? 'white' : 'rgb(var(--muted))', border: `1px solid ${selectedSeason === s ? 'rgb(var(--crimson))' : 'rgba(var(--border), var(--border-a))'}` }}>
                          S{s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="overflow-y-auto flex-1">
                  {episodesForSeason.map((ep, idx) => <EpisodeRow key={ep.id || idx} ep={ep} idx={idx} />)}
                </div>
              </div>
            )}

            {/* About */}
            {mobileTab === 'player' && (
              <div className="mt-4">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2 py-1 rounded font-mono text-[9px] font-bold tracking-wider uppercase"
                    style={{ background: isTV ? 'rgba(var(--blue), 0.1)' : 'rgba(var(--crimson), 0.1)', color: isTV ? 'rgb(var(--blue))' : 'rgb(var(--crimson))' }}>
                    {itemDetails.type}
                  </span>
                  {itemDetails.releaseDate && <span className="flex items-center gap-1 text-[13px]" style={{ color: 'rgb(var(--muted))' }}><Calendar size={13} />{itemDetails.releaseDate}</span>}
                  {itemDetails.duration && <span className="flex items-center gap-1 text-[13px]" style={{ color: 'rgb(var(--muted))' }}><Clock size={13} />{itemDetails.duration}</span>}
                </div>
                <h1 className="font-heading font-black italic tracking-tight uppercase leading-none mb-3" style={{ color: 'rgb(var(--fg))', fontSize: 'clamp(24px, 4vw, 36px)' }}>{itemDetails.title}</h1>
                {itemDetails.description && <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'rgb(var(--muted))' }}>{itemDetails.description}</p>}
                {itemDetails.genres?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {itemDetails.genres.map((g, i) => (
                      <span key={i} className="px-2.5 py-1 rounded text-[12px]" style={{ background: 'rgba(var(--border), 0.06)', border: '1px solid rgba(var(--border), var(--border-a))', color: 'rgb(var(--muted))' }}>{g}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Desktop Sidebar */}
          {hasSidebar && (
            <aside
              className="hidden lg:flex sticky flex-col rounded-xl overflow-hidden"
              style={{ top: 'calc(var(--hdr) + 12px)', height: 'calc(100vh - var(--hdr) - 24px)', background: 'rgb(var(--surface))', border: '1px solid rgba(var(--border), var(--border-a))' }}
              data-testid="episodes-sidebar"
            >
              <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ borderBottom: '1px solid rgba(var(--border), var(--border-a))' }}>
                <span className="font-heading font-bold italic text-base tracking-wide uppercase" style={{ color: 'rgb(var(--fg))' }}>Episodes</span>
                <span className="font-mono text-[9px] px-2 py-0.5 rounded" style={{ color: 'rgb(var(--muted))', background: 'rgba(var(--border), 0.06)', border: '1px solid rgba(var(--border), var(--border-a))' }}>{episodesForSeason.length}</span>
              </div>
              {getSeasons().length > 1 && (
                <div className="flex flex-wrap gap-1 px-3 py-2" style={{ borderBottom: '1px solid rgba(var(--border), var(--border-a))' }}>
                  {getSeasons().map(s => (
                    <button key={s} onClick={() => handleSeasonChange(s)} className="px-3 py-1 rounded-full font-mono text-[9px] font-bold tracking-wider uppercase transition-all"
                      style={{ background: selectedSeason === s ? 'rgb(var(--crimson))' : 'transparent', color: selectedSeason === s ? 'white' : 'rgb(var(--muted))', border: `1px solid ${selectedSeason === s ? 'rgb(var(--crimson))' : 'rgba(var(--border), var(--border-a))'}` }}>
                      Season {s}
                    </button>
                  ))}
                </div>
              )}
              <div className="px-4 py-1.5 flex-shrink-0 font-mono text-[9px] tracking-wider uppercase" style={{ color: 'rgb(var(--muted))' }}>
                Season {selectedSeason} - {episodesForSeason.length} eps
              </div>
              <div className="overflow-y-auto flex-1">
                {episodesForSeason.map((ep, idx) => <EpisodeRow key={ep.id || idx} ep={ep} idx={idx} />)}
              </div>
            </aside>
          )}
        </div>
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// V2 Popup (kept as-is per user request)
// ═══════════════════════════════════════════════════
function V2Popup({ onClose }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-[600]" style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(6px)' }} onClick={onClose}>
      <div className="pop-in w-[90%] max-w-[520px] rounded-2xl overflow-hidden" style={{ background: 'rgb(var(--surface))', border: '1px solid rgba(var(--border), var(--border-a))' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-4 pb-3">
          <div>
            <div className="font-heading font-black italic text-xl tracking-wide uppercase text-gradient-crimson leading-tight">Povertymovie V2 is here</div>
            <div className="text-[12px] mt-1 font-mono" style={{ color: 'rgb(var(--muted))' }}>v2.povertymovie.fun</div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-md flex items-center justify-center transition-all"
            style={{ background: 'rgba(var(--border), 0.06)', border: '1px solid rgba(var(--border), var(--border-a))', color: 'rgb(var(--muted))' }} data-testid="v2-popup-close">
            <X size={13} />
          </button>
        </div>
        <img src="/preview.png" alt="" className="w-full block object-cover max-h-[260px]" style={{ borderTop: '1px solid rgba(var(--border), var(--border-a))', borderBottom: '1px solid rgba(var(--border), var(--border-a))', background: 'rgba(var(--border), 0.04)' }} loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
        <div className="p-4 pt-3.5">
          <p className="text-[13.5px] leading-relaxed mb-4" style={{ color: 'rgb(var(--muted))' }}>The new version of Povertymovie is out - its lowkey just better</p>
          <div className="flex items-center justify-between gap-3">
            <a href="https://v2.povertymovie.fun" target="_blank" rel="noopener noreferrer" onClick={onClose}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-heading font-bold italic text-base tracking-wide uppercase text-white transition-opacity hover:opacity-85"
              style={{ background: 'linear-gradient(118deg, #e03010, #cc44ff)' }} data-testid="v2-goto-btn">
              Open V2 <ChevronRight size={16} />
            </a>
            <button onClick={onClose} className="text-[12px] transition-colors" style={{ color: 'rgb(var(--muted))' }} data-testid="v2-dismiss-btn">Maybe later</button>
          </div>
        </div>
      </div>
    </div>
  );
}